import "./style.css";

const GAME_VERSION = "v2-falling";
const BOARD_WIDTH = 8;
const BOARD_HEIGHT = 12;
const CELL_COUNT = BOARD_WIDTH * BOARD_HEIGHT;
const START_SCORE = 315;
const START_BEST = 9527;
const DROP_INTERVAL_MS = 620;

type TileColor = "blue" | "orange" | "green" | "purple" | "yellow" | "cyan" | "red";
type GamePhase = "ready" | "falling" | "paused" | "game-over";
type BoardCell = TileColor | null;

type Shape = {
  id: string;
  label: string;
  cells: Array<[number, number]>;
};

type FallingPiece = {
  id: number;
  shape: Shape;
  color: TileColor;
  x: number;
  y: number;
};

declare global {
  interface Window {
    __BLOCK_BLAST_V2_DEBUG__?: {
      dropTick: () => void;
      forceLineClear: () => void;
      hardDrop: () => void;
      moveLeft: () => void;
      moveRight: () => void;
      reset: () => void;
      rotate: () => void;
      snapshot: () => {
        phase: GamePhase;
        score: number;
        best: number;
        boardCells: number;
        occupied: number;
        activeCells: number;
        activeY: number;
        nextShape: string;
        linesCleared: number;
        version: string;
      };
    };
  }
}

const TILE_META: Record<TileColor, { label: string; main: string; high: string; dark: string }> = {
  blue: { label: "Blue", main: "#226cf2", high: "#43a5ff", dark: "#1244b8" },
  orange: { label: "Orange", main: "#ff5b18", high: "#ff9b3a", dark: "#c8320e" },
  green: { label: "Green", main: "#15d63b", high: "#51ff60", dark: "#0b8f22" },
  purple: { label: "Purple", main: "#ad5ee8", high: "#d08cff", dark: "#7737b5" },
  yellow: { label: "Yellow", main: "#ffd11b", high: "#fff05e", dark: "#d79505" },
  cyan: { label: "Cyan", main: "#25cbe6", high: "#6ef7ff", dark: "#1094b5" },
  red: { label: "Red", main: "#f23835", high: "#ff6864", dark: "#b51d1e" }
};

const SHAPES: Shape[] = [
  { id: "square", label: "Square", cells: [[0, 0], [1, 0], [0, 1], [1, 1]] },
  { id: "line", label: "Line", cells: [[0, 0], [1, 0], [2, 0], [3, 0]] },
  { id: "tee", label: "T", cells: [[0, 0], [1, 0], [2, 0], [1, 1]] },
  { id: "left-step", label: "Step", cells: [[1, 0], [2, 0], [0, 1], [1, 1]] },
  { id: "right-step", label: "Step", cells: [[0, 0], [1, 0], [1, 1], [2, 1]] },
  { id: "left-hook", label: "Hook", cells: [[0, 0], [0, 1], [1, 1], [2, 1]] },
  { id: "right-hook", label: "Hook", cells: [[2, 0], [0, 1], [1, 1], [2, 1]] }
];

const COLOR_SEQUENCE: TileColor[] = ["blue", "yellow", "green", "purple", "red", "cyan", "orange"];

const app = mustGetElement("app");

let board: BoardCell[] = createEmptyBoard();
let phase: GamePhase = "ready";
let score = START_SCORE;
let best = START_BEST;
let linesCleared = 0;
let pieceSerial = 0;
let activePiece: FallingPiece = createPiece(0);
let nextPiece: FallingPiece = createPiece(1);
let dropTimer: number | null = null;
let sparkSerial = 0;

app.innerHTML = `
  <section class="blast-card" aria-label="Falling Block Blast game">
    <div class="hud" aria-live="polite">
      <div class="best-score" aria-label="Best score">
        <span class="crown" aria-hidden="true">♛</span>
        <span id="best-score">${START_BEST}</span>
      </div>
      <div id="score" class="score">${START_SCORE}</div>
      <button id="settings-button" class="settings-button" type="button" aria-label="Settings">⚙</button>
    </div>

    <div class="board-area">
      <div id="spark-layer" class="spark-layer" aria-hidden="true"></div>
      <div id="board" class="board" role="grid" aria-label="8 by 12 falling block board"></div>
      <div id="status-pill" class="status-pill">READY</div>
    </div>

    <div class="play-footer" aria-label="Falling block controls">
      <div class="next-panel" aria-label="Next block">
        <span>Next</span>
        <div id="next-piece" class="next-piece"></div>
      </div>
      <div class="control-pad">
        <button id="left-button" type="button" aria-label="Move left">◀</button>
        <button id="rotate-button" type="button" aria-label="Rotate">↻</button>
        <button id="right-button" type="button" aria-label="Move right">▶</button>
        <button id="down-button" type="button" aria-label="Soft drop">▼</button>
        <button id="drop-button" type="button" aria-label="Hard drop">⤓</button>
      </div>
    </div>

    <div id="settings-panel" class="settings-panel" hidden>
      <button id="start-button" type="button">Start</button>
      <button id="pause-button" type="button">Pause</button>
      <button id="new-game-button" type="button">New Game</button>
      <span id="version-label">${GAME_VERSION}</span>
    </div>
  </section>
`;

const boardEl = mustGetElement("board");
const scoreEl = mustGetElement("score");
const bestScoreEl = mustGetElement("best-score");
const sparkLayer = mustGetElement("spark-layer");
const statusPill = mustGetElement("status-pill");
const nextPieceEl = mustGetElement("next-piece");
const settingsButton = mustGetElement<HTMLButtonElement>("settings-button");
const settingsPanel = mustGetElement("settings-panel");
const startButton = mustGetElement<HTMLButtonElement>("start-button");
const pauseButton = mustGetElement<HTMLButtonElement>("pause-button");
const newGameButton = mustGetElement<HTMLButtonElement>("new-game-button");
const leftButton = mustGetElement<HTMLButtonElement>("left-button");
const rightButton = mustGetElement<HTMLButtonElement>("right-button");
const rotateButton = mustGetElement<HTMLButtonElement>("rotate-button");
const downButton = mustGetElement<HTMLButtonElement>("down-button");
const dropButton = mustGetElement<HTMLButtonElement>("drop-button");

settingsButton.addEventListener("click", () => {
  settingsPanel.hidden = !settingsPanel.hidden;
});
startButton.addEventListener("click", () => {
  startGame();
  settingsPanel.hidden = true;
});
pauseButton.addEventListener("click", () => {
  togglePause();
  settingsPanel.hidden = true;
});
newGameButton.addEventListener("click", () => {
  resetGame();
  startGame();
  settingsPanel.hidden = true;
});
leftButton.addEventListener("click", () => moveActive(-1, 0));
rightButton.addEventListener("click", () => moveActive(1, 0));
rotateButton.addEventListener("click", rotateActive);
downButton.addEventListener("click", () => softDrop());
dropButton.addEventListener("click", hardDrop);
document.addEventListener("keydown", onKeyDown);
document.addEventListener("visibilitychange", () => {
  if (document.hidden && phase === "falling") pauseGame();
});

installDebugHook();
render();

function startGame(): void {
  if (phase === "game-over") resetGame();
  phase = "falling";
  ensureTimer();
  render();
}

function resetGame(): void {
  stopTimer();
  board = createEmptyBoard();
  score = START_SCORE;
  linesCleared = 0;
  pieceSerial = 0;
  activePiece = createPiece(0);
  nextPiece = createPiece(1);
  phase = "ready";
  sparkLayer.innerHTML = "";
  render();
}

function togglePause(): void {
  if (phase === "falling") {
    pauseGame();
    return;
  }
  if (phase === "paused" || phase === "ready") startGame();
}

function pauseGame(): void {
  phase = "paused";
  stopTimer();
  render();
}

function ensureTimer(): void {
  if (dropTimer !== null) return;
  dropTimer = window.setInterval(() => dropTick(), DROP_INTERVAL_MS);
}

function stopTimer(): void {
  if (dropTimer === null) return;
  window.clearInterval(dropTimer);
  dropTimer = null;
}

function onKeyDown(event: KeyboardEvent): void {
  const handledKeys = new Set(["ArrowLeft", "ArrowRight", "ArrowDown", "ArrowUp", " ", "Enter", "p", "P"]);
  if (!handledKeys.has(event.key)) return;
  event.preventDefault();

  if (event.key === "Enter") startGame();
  if (event.key === "p" || event.key === "P") togglePause();
  if (event.key === "ArrowLeft") moveActive(-1, 0);
  if (event.key === "ArrowRight") moveActive(1, 0);
  if (event.key === "ArrowDown") softDrop();
  if (event.key === "ArrowUp") rotateActive();
  if (event.key === " ") hardDrop();
}

function createPiece(offset: number): FallingPiece {
  const serial = pieceSerial + offset;
  const shape = SHAPES[serial % SHAPES.length];
  const color = COLOR_SEQUENCE[(serial * 2 + 1) % COLOR_SEQUENCE.length];
  return {
    id: serial,
    shape,
    color,
    x: Math.floor((BOARD_WIDTH - shapeWidth(shape)) / 2),
    y: 0
  };
}

function spawnNextPiece(): void {
  pieceSerial += 1;
  activePiece = {
    ...nextPiece,
    x: Math.floor((BOARD_WIDTH - shapeWidth(nextPiece.shape)) / 2),
    y: 0
  };
  nextPiece = createPiece(1);

  if (!canPlace(activePiece, activePiece.x, activePiece.y)) {
    phase = "game-over";
    stopTimer();
  }
}

function dropTick(): void {
  if (phase !== "falling") return;
  if (canPlace(activePiece, activePiece.x, activePiece.y + 1)) {
    activePiece = { ...activePiece, y: activePiece.y + 1 };
    score += 1;
    best = Math.max(best, score);
    render();
    return;
  }

  lockActivePiece();
}

function softDrop(): void {
  if (phase === "ready") startGame();
  if (phase !== "falling") return;
  if (canPlace(activePiece, activePiece.x, activePiece.y + 1)) {
    activePiece = { ...activePiece, y: activePiece.y + 1 };
    score += 4;
    best = Math.max(best, score);
    render();
    return;
  }
  lockActivePiece();
}

function hardDrop(): void {
  if (phase === "ready") startGame();
  if (phase !== "falling") return;
  let distance = 0;
  while (canPlace(activePiece, activePiece.x, activePiece.y + 1)) {
    activePiece = { ...activePiece, y: activePiece.y + 1 };
    distance += 1;
  }
  score += distance * 9;
  lockActivePiece();
}

function moveActive(dx: number, dy: number): void {
  if (phase === "ready") startGame();
  if (phase !== "falling") return;
  const nextX = activePiece.x + dx;
  const nextY = activePiece.y + dy;
  if (!canPlace(activePiece, nextX, nextY)) return;
  activePiece = { ...activePiece, x: nextX, y: nextY };
  render();
}

function rotateActive(): void {
  if (phase === "ready") startGame();
  if (phase !== "falling") return;
  if (activePiece.shape.id === "square") return;

  const rotatedShape: Shape = {
    ...activePiece.shape,
    cells: normalizeCells(activePiece.shape.cells.map(([x, y]) => [y, -x]))
  };
  const candidates = [0, -1, 1, -2, 2];
  for (const offsetX of candidates) {
    if (canPlace({ ...activePiece, shape: rotatedShape }, activePiece.x + offsetX, activePiece.y)) {
      activePiece = { ...activePiece, shape: rotatedShape, x: activePiece.x + offsetX };
      render();
      return;
    }
  }
}

function lockActivePiece(): void {
  const indexes = pieceIndexes(activePiece, activePiece.x, activePiece.y);
  for (const index of indexes) board[index] = activePiece.color;
  spawnSparkles(indexes);

  const result = clearFullLines();
  if (result.lines > 0) {
    linesCleared += result.lines;
    score += result.lines * 180 + result.cells * 7;
    spawnSparkles(result.indexes);
  } else {
    score += indexes.length * 12;
  }

  best = Math.max(best, score);
  spawnNextPiece();
  render();
}

function clearFullLines(): { lines: number; cells: number; indexes: number[] } {
  const rowsToClear: number[] = [];
  for (let y = 0; y < BOARD_HEIGHT; y += 1) {
    const row = Array.from({ length: BOARD_WIDTH }, (_, x) => toIndex(x, y));
    if (row.every((index) => board[index])) rowsToClear.push(y);
  }

  if (rowsToClear.length === 0) return { lines: 0, cells: 0, indexes: [] };

  const indexes = rowsToClear.flatMap((y) => Array.from({ length: BOARD_WIDTH }, (_, x) => toIndex(x, y)));
  const remainingRows: BoardCell[][] = [];
  for (let y = BOARD_HEIGHT - 1; y >= 0; y -= 1) {
    if (!rowsToClear.includes(y)) {
      remainingRows.unshift(Array.from({ length: BOARD_WIDTH }, (_, x) => board[toIndex(x, y)]));
    }
  }

  while (remainingRows.length < BOARD_HEIGHT) {
    remainingRows.unshift(Array.from({ length: BOARD_WIDTH }, () => null));
  }

  board = remainingRows.flat();
  return { lines: rowsToClear.length, cells: indexes.length, indexes };
}

function forceLineClear(): void {
  for (let x = 0; x < BOARD_WIDTH; x += 1) board[toIndex(x, BOARD_HEIGHT - 1)] = "yellow";
  const result = clearFullLines();
  if (result.lines > 0) {
    linesCleared += result.lines;
    score += result.lines * 220;
    best = Math.max(best, score);
    spawnSparkles(result.indexes);
  }
  render();
}

function canPlace(piece: FallingPiece, x: number, y: number): boolean {
  return piece.shape.cells.every(([dx, dy]) => {
    const targetX = x + dx;
    const targetY = y + dy;
    return insideBoard(targetX, targetY) && board[toIndex(targetX, targetY)] === null;
  });
}

function pieceIndexes(piece: FallingPiece, x: number, y: number): number[] {
  return piece.shape.cells.map(([dx, dy]) => toIndex(x + dx, y + dy));
}

function render(): void {
  renderBoard();
  renderNextPiece();
  updateHud();
}

function renderBoard(): void {
  const activeIndexes = new Map<number, TileColor>();
  if (phase !== "game-over") {
    for (const index of pieceIndexes(activePiece, activePiece.x, activePiece.y)) {
      activeIndexes.set(index, activePiece.color);
    }
  }

  const ghostIndexes = new Set<number>();
  if (phase === "falling") {
    let ghostY = activePiece.y;
    while (canPlace(activePiece, activePiece.x, ghostY + 1)) ghostY += 1;
    for (const index of pieceIndexes(activePiece, activePiece.x, ghostY)) ghostIndexes.add(index);
  }

  boardEl.innerHTML = "";
  boardEl.classList.toggle("is-over", phase === "game-over");
  boardEl.classList.toggle("is-paused", phase === "paused");

  for (let index = 0; index < CELL_COUNT; index += 1) {
    const cell = document.createElement("div");
    const lockedColor = board[index];
    const activeColor = activeIndexes.get(index) ?? null;
    cell.className = "board-cell";
    cell.setAttribute("role", "gridcell");
    cell.setAttribute("aria-label", `${cellName(index)} ${activeColor ? "Falling" : lockedColor ? TILE_META[lockedColor].label : "Empty"}`);

    if (activeColor) {
      cell.classList.add("active-cell");
      cell.append(createTile(activeColor));
    } else if (lockedColor) {
      cell.append(createTile(lockedColor));
    } else {
      cell.classList.add("empty");
      if (ghostIndexes.has(index)) cell.classList.add("ghost-cell");
    }

    boardEl.append(cell);
  }
}

function renderNextPiece(): void {
  nextPieceEl.innerHTML = "";
  nextPieceEl.append(renderMiniPiece(nextPiece));
}

function renderMiniPiece(piece: FallingPiece): HTMLElement {
  const bounds = shapeBounds(piece.shape);
  const grid = document.createElement("div");
  grid.className = "mini-piece";
  grid.style.gridTemplateColumns = `repeat(${bounds.width}, var(--mini-cell))`;
  grid.style.gridTemplateRows = `repeat(${bounds.height}, var(--mini-cell))`;

  const occupied = new Set(piece.shape.cells.map(([x, y]) => `${x}:${y}`));
  for (let y = 0; y < bounds.height; y += 1) {
    for (let x = 0; x < bounds.width; x += 1) {
      const node = document.createElement("span");
      if (occupied.has(`${x}:${y}`)) node.append(createTile(piece.color, "tile mini-tile"));
      grid.append(node);
    }
  }

  return grid;
}

function createTile(color: TileColor, className = "tile"): HTMLElement {
  const tile = document.createElement("span");
  tile.className = className;
  tile.style.setProperty("--tile-main", TILE_META[color].main);
  tile.style.setProperty("--tile-high", TILE_META[color].high);
  tile.style.setProperty("--tile-dark", TILE_META[color].dark);
  return tile;
}

function updateHud(): void {
  scoreEl.textContent = String(score);
  bestScoreEl.textContent = String(best);
  statusPill.textContent = statusText();
  startButton.textContent = phase === "falling" ? "Running" : "Start";
  pauseButton.textContent = phase === "paused" ? "Resume" : "Pause";
  app.dataset.phase = phase;
  app.dataset.gameVersion = GAME_VERSION;
}

function statusText(): string {
  if (phase === "ready") return "START";
  if (phase === "falling") return "FALLING";
  if (phase === "paused") return "PAUSED";
  return "GAME OVER";
}

function spawnSparkles(indexes: number[]): void {
  const rect = boardEl.getBoundingClientRect();
  const hostRect = sparkLayer.getBoundingClientRect();
  const cellWidth = rect.width / BOARD_WIDTH;
  const cellHeight = rect.height / BOARD_HEIGHT;
  const limitedIndexes = indexes.slice(0, 20);

  for (const index of limitedIndexes) {
    sparkSerial += 1;
    const x = index % BOARD_WIDTH;
    const y = Math.floor(index / BOARD_WIDTH);
    const sparkle = document.createElement("span");
    sparkle.className = "spark";
    sparkle.style.left = `${rect.left - hostRect.left + x * cellWidth + cellWidth * 0.5}px`;
    sparkle.style.top = `${rect.top - hostRect.top + y * cellHeight + cellHeight * 0.5}px`;
    sparkle.style.animationDelay = `${(sparkSerial % 5) * 38}ms`;
    sparkLayer.append(sparkle);
    window.setTimeout(() => sparkle.remove(), 920);
  }
}

function installDebugHook(): void {
  window.__BLOCK_BLAST_V2_DEBUG__ = {
    dropTick,
    forceLineClear,
    hardDrop,
    moveLeft: () => moveActive(-1, 0),
    moveRight: () => moveActive(1, 0),
    reset: resetGame,
    rotate: rotateActive,
    snapshot: () => ({
      phase,
      score,
      best,
      boardCells: CELL_COUNT,
      occupied: board.filter(Boolean).length,
      activeCells: activePiece.shape.cells.length,
      activeY: activePiece.y,
      nextShape: nextPiece.shape.id,
      linesCleared,
      version: GAME_VERSION
    })
  };
}

function createEmptyBoard(): BoardCell[] {
  return Array.from({ length: CELL_COUNT }, () => null);
}

function normalizeCells(cells: Array<[number, number]>): Array<[number, number]> {
  const minX = Math.min(...cells.map(([x]) => x));
  const minY = Math.min(...cells.map(([, y]) => y));
  return cells.map(([x, y]) => [x - minX, y - minY]);
}

function shapeBounds(shape: Shape): { width: number; height: number } {
  return {
    width: Math.max(...shape.cells.map(([x]) => x)) + 1,
    height: Math.max(...shape.cells.map(([, y]) => y)) + 1
  };
}

function shapeWidth(shape: Shape): number {
  return shapeBounds(shape).width;
}

function cellName(index: number): string {
  return `Row ${Math.floor(index / BOARD_WIDTH) + 1}, column ${(index % BOARD_WIDTH) + 1}`;
}

function insideBoard(x: number, y: number): boolean {
  return x >= 0 && x < BOARD_WIDTH && y >= 0 && y < BOARD_HEIGHT;
}

function toIndex(x: number, y: number): number {
  return y * BOARD_WIDTH + x;
}

function mustGetElement<T extends HTMLElement = HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing element #${id}`);
  return element as T;
}
